import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import axiosInstance from "../../../axiosInstance";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { getJobPortalAuthConfig, getJobPortalSession } from "../../../helpers/jobPortalAuth";
import JobFiltersPanel from "../../../components/job/JobFiltersPanel";

const DEFAULT_FILTERS = {
  keyword: "",
  jobTitle: "",
  jobRole: "",
  location: "",
  latitude: "",
  longitude: "",
  radiusKm: "",
  department: "",
  skill: "",
  employmentType: "",
  workMode: "",
  experienceMin: "",
  experienceMax: "",
  salaryMin: "",
  salaryMax: "",
  sort: "relevant",
};

const WORK_MODE_COLORS = {
  remote: "success",
  onsite: "primary",
  hybrid: "warning",
};

const EMPLOYMENT_COLORS = {
  full_time: "info",
  part_time: "secondary",
  contract: "danger",
  internship: "warning",
};

const formatSalary = (value) => {
  if (!value || value === 0) return "N/A";
  if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(0)}K`;
  return `₹${value}`;
};

const formatLabel = (str) =>
  str ? str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "";

const SkeletonCard = () => (
  <div className="col-md-6">
    <div className="card border-0 shadow-sm h-100 placeholder-glow">
      <div className="card-body p-4">
        <div className="d-flex gap-3 align-items-start mb-3">
          <span className="placeholder rounded-3" style={{ width: 52, height: 52 }} />
          <div className="flex-grow-1">
            <span className="placeholder col-7 d-block mb-2 rounded" />
            <span className="placeholder col-5 d-block rounded" />
          </div>
        </div>
        <span className="placeholder col-12 d-block mb-2 rounded" />
        <span className="placeholder col-9 d-block mb-3 rounded" />
        <div className="d-flex gap-2">
          <span className="placeholder col-3 rounded-pill" style={{ height: 24 }} />
          <span className="placeholder col-3 rounded-pill" style={{ height: 24 }} />
          <span className="placeholder col-3 rounded-pill" style={{ height: 24 }} />
        </div>
      </div>
    </div>
  </div>
);

const JobCard = ({ job, isSaved, isApplied, canApply, onSave, onApply }) => {
  const initials = (job.company?.name || "CO")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const avatarColors = [
    ["#e8f4fd", "#1a73e8"],
    ["#fce8f3", "#c2185b"],
    ["#e8f5e9", "#388e3c"],
    ["#fff3e0", "#f57c00"],
    ["#ede7f6", "#7b1fa2"],
  ];
  const colorIdx = (job.title?.charCodeAt(0) || 0) % avatarColors.length;
  const [bg, fg] = avatarColors[colorIdx];

  return (
    <div className="col-md-6">
      <div
        className="card border-0 shadow-sm h-100 job-card"
        style={{
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          cursor: "default",
        }}
      >
        <div className="card-body p-4 d-flex flex-column gap-3">
          {/* Header */}
          <div className="d-flex gap-3 align-items-start">
            <div
              className="rounded-3 d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
              style={{
                width: 52,
                height: 52,
                background: bg,
                color: fg,
                fontSize: 16,
                letterSpacing: 1,
              }}
            >
              {initials}
            </div>
            <div className="flex-grow-1 min-w-0">
              <h6 className="mb-0 fw-semibold text-truncate" style={{ fontSize: 15 }}>
                {job.title}
              </h6>
              <div className="text-muted small mt-1 text-truncate">
                <i className="bi bi-building me-1" />
                {job.company?.name || "Company"}
                <span className="mx-2">·</span>
                <i className="bi bi-geo-alt me-1" />
                {job.location?.display || "Location TBD"}
              </div>
            </div>
            <button
              className={`btn btn-sm flex-shrink-0 ${isSaved ? "btn-success" : "btn-outline-secondary"}`}
              style={{ borderRadius: 8, padding: "4px 10px" }}
              onClick={() => onSave(job.id)}
              title={isSaved ? "Unsave job" : "Save job"}
            >
              <i className={`bi ${isSaved ? "bi-bookmark-fill" : "bi-bookmark"}`} />
            </button>
          </div>

          {/* Salary */}
          <div className="d-flex align-items-center gap-1 text-success fw-semibold small">
            <i className="bi bi-currency-rupee" />
            {formatSalary(job.salary?.minimum)} – {formatSalary(job.salary?.maximum)}
            <span className="text-muted fw-normal ms-1">/ yr</span>
          </div>

          {/* Description */}
          <p
            className="text-muted mb-0 small"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              lineHeight: 1.6,
            }}
          >
            {job.description || "No description provided for this role."}
          </p>

          {/* Badges */}
          <div className="d-flex flex-wrap gap-2">
            {job.workMode && (
              <span
                className={`badge text-bg-${WORK_MODE_COLORS[job.workMode] || "secondary"}`}
                style={{ borderRadius: 6, fontWeight: 500, fontSize: 11 }}
              >
                {formatLabel(job.workMode)}
              </span>
            )}
            {job.employmentType && (
              <span
                className={`badge text-bg-${EMPLOYMENT_COLORS[job.employmentType] || "secondary"}`}
                style={{ borderRadius: 6, fontWeight: 500, fontSize: 11 }}
              >
                {formatLabel(job.employmentType)}
              </span>
            )}
            {(job.skills || []).slice(0, 3).map((skill) => (
              <span
                key={skill}
                className="badge"
                style={{
                  borderRadius: 6,
                  fontWeight: 500,
                  fontSize: 11,
                  background: "#f1f3f5",
                  color: "#495057",
                }}
              >
                {skill}
              </span>
            ))}
            {(job.skills || []).length > 3 && (
              <span
                className="badge"
                style={{
                  borderRadius: 6,
                  fontWeight: 500,
                  fontSize: 11,
                  background: "#f1f3f5",
                  color: "#868e96",
                }}
              >
                +{job.skills.length - 3} more
              </span>
            )}
          </div>

          {/* Footer */}
          <div className="mt-auto pt-2 border-top d-flex justify-content-end">
            <button
              className={`btn btn-sm ${
                isApplied ? "btn-success text-white" : canApply ? "btn-primary" : "btn-secondary"
              }`}
              style={{ borderRadius: 8, minWidth: 110, fontWeight: 500 }}
              disabled={isApplied}
              onClick={() => onApply(job.id)}
            >
              {isApplied ? (
                <>
                  <i className="bi bi-check2-circle me-1" /> Applied
                </>
              ) : !canApply ? (
                <>
                  <i className="bi bi-credit-card me-1" /> Buy Plan
                </>
              ) : (
                <>
                  <i className="bi bi-send me-1" /> Apply Now
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SeekerJobs = () => {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [savedJobs, setSavedJobs] = useState([]);
  const [appliedJobIds, setAppliedJobIds] = useState(new Set());
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState(null);
  const [subscription, setSubscription] = useState(null);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      await getJobPortalSession();
      const authConfig = await getJobPortalAuthConfig();
      const params = new URLSearchParams();
      params.set("limit", "12");
      params.set("page", String(page));
      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (
          key === "location" &&
          appliedFilters.latitude &&
          appliedFilters.longitude &&
          appliedFilters.radiusKm
        ) {
          return;
        }
        if (value) params.set(key, value);
      });

      const [
        jobsResponse,
        applicationsResponse,
        savedJobsResponse,
        specializationResponse,
        departmentResponse,
        subscriptionResponse,
      ] = await Promise.all([
        axiosInstance.get(`/job-portal/jobs/public?${params.toString()}`),
        axiosInstance.get("/job-portal/applications/me?limit=100", authConfig),
        axiosInstance.get("/job-portal/jobs/saved", authConfig),
        axiosInstance.get("/get-all-specialization"),
        axiosInstance.get("/get-all-department"),
        axiosInstance.get("/job-portal/subscription/me", authConfig),
      ]);

      const nextJobs = jobsResponse.data?.data?.jobs || [];
      const applications = applicationsResponse.data?.data?.applications || [];
      const allCategories = [
        ...(departmentResponse.data?.Department || []).map((i) => i.name),
      ];
      const allJobRoles = specializationResponse.data?.Specialization || [];

      setJobs(nextJobs);
      setMeta(jobsResponse.data?.meta || null);
      setCategories([...new Set(allCategories)].filter(Boolean));
      setJobRoles(allJobRoles);
      setSavedJobs(savedJobsResponse.data?.data?.jobs?.map((j) => j.id) || []);
      setAppliedJobIds(new Set(applications.map((a) => a.jobId)));
      setSubscription(subscriptionResponse.data?.data?.subscription || null);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Unable to load jobs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [page, appliedFilters]);

  const visibleJobs = useMemo(() => jobs, [jobs]);

  const applyToJob = async (jobId) => {
    if (subscription?.status !== "active") {
      toast.error("An active job seeker plan is required to apply");
      window.location.href = "/job-seeker/plans";
      return;
    }

    try {
      const authConfig = await getJobPortalAuthConfig();
      await axiosInstance.post(`/job-portal/jobs/${jobId}/apply`, {}, authConfig);
      toast.success("Application submitted!");
      setAppliedJobIds((prev) => new Set(prev).add(jobId));
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to apply");
    }
  };

  const toggleSave = (jobId) => {
    const run = async () => {
      try {
        const authConfig = await getJobPortalAuthConfig();
        if (savedJobs.includes(jobId)) {
          const res = await axiosInstance.delete(`/job-portal/jobs/${jobId}/save`, authConfig);
          setSavedJobs(res.data?.data?.savedJobIds || []);
          toast.success("Job removed from saved list");
        } else {
          const res = await axiosInstance.put(`/job-portal/jobs/${jobId}/save`, {}, authConfig);
          setSavedJobs(res.data?.data?.savedJobIds || []);
          toast.success("Job saved!");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Unable to update saved jobs");
      }
    };
    run();
  };

  const handleFilterChange = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const applyFilters = (e) => {
    e.preventDefault();
    setPage(1);
    setAppliedFilters(filters);
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setPage(1);
  };

  const totalPages = meta?.totalPages || 1;
  const currentPage = meta?.page || page;

  return (
    <>
      <style>{`
        .job-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.10) !important;
        }
        .page-btn {
          width: 38px; height: 38px; padding: 0;
          border-radius: 8px; font-size: 14px;
          display: inline-flex; align-items: center; justify-content: center;
        }
        .sort-bar select { border-radius: 10px; font-size: 14px; }
        .results-meta { font-size: 13px; }
      `}</style>

        <Sidebar />
      <main className="content">
        <Navbar />

        {subscription?.status !== "active" && !loading && (
          <div className="alert alert-warning border-0 shadow-sm mt-4 mb-0">
            An active job seeker plan is required to apply for jobs.
            <Link to="/job-seeker/plans" className="ms-2 alert-link">
              Buy Plan
            </Link>
          </div>
        )}

        {/* Page Header */}
        <div className="py-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h4 className="fw-bold mb-1" style={{ letterSpacing: "-0.3px" }}>
                <i className="bi bi-briefcase-fill text-primary me-2" />
                Browse Jobs
              </h4>
              <p className="text-muted mb-0 small">
                Explore open roles, save your favourites, and apply in one click.
              </p>
            </div>
            <Link to="/job-seeker/saved-jobs" className="btn btn-outline-success">
              <i className="bi bi-bookmark-heart me-2" />
              Saved Jobs
              {savedJobs.length > 0 && (
                <span className="badge bg-success ms-2">{savedJobs.length}</span>
              )}
            </Link>
          </div>
        </div>

        <div className="row g-4 align-items-start">
          {/* Filters Sidebar */}
          <div className="col-xl-3 col-lg-4">
            <div className="sticky-top" style={{ top: 80 }}>
              <JobFiltersPanel
                filters={filters}
                onChange={handleFilterChange}
                onSubmit={applyFilters}
                onReset={resetFilters}
                categories={categories}
                jobRoles={jobRoles}
                loading={loading}
                compact
              />
            </div>
          </div>

          {/* Jobs Grid */}
          <div className="col-xl-9 col-lg-8">
            {/* Toolbar */}
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
              <div className="results-meta text-muted">
                {loading ? (
                  <span className="placeholder col-6 rounded" />
                ) : (
                  <>
                    <span className="fw-semibold text-dark">{visibleJobs.length}</span> jobs
                    on page{" "}
                    <span className="fw-semibold text-dark">{currentPage}</span> of{" "}
                    <span className="fw-semibold text-dark">{totalPages}</span>
                  </>
                )}
              </div>
              <div className="sort-bar" style={{ minWidth: 200 }}>
                <select
                  className="form-select form-select-sm"
                  value={filters.sort}
                  onChange={(e) => {
                    const next = { ...filters, sort: e.target.value };
                    setFilters(next);
                    setAppliedFilters(next);
                    setPage(1);
                  }}
                >
                  <option value="relevant"> Most Relevant</option>
                  <option value="newest"> Newest First</option>
                  <option value="oldest"> Oldest First</option>
                  <option value="salary_high"> Salary: High → Low</option>
                  <option value="salary_low"> Salary: Low → High</option>
                  <option value="title_asc"> Title A–Z</option>
                </select>
              </div>
            </div>

            {/* Job Cards */}
            {loading ? (
              <div className="row g-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            ) : visibleJobs.length === 0 ? (
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5">
                  <i
                    className="bi bi-search text-muted"
                    style={{ fontSize: 48, opacity: 0.35 }}
                  />
                  <h6 className="mt-3 mb-1 text-muted">No jobs found</h6>
                  <p className="text-muted small mb-3">
                    Try adjusting or resetting your filters.
                  </p>
                  <button className="btn btn-outline-primary btn-sm" onClick={resetFilters}>
                    <i className="bi bi-arrow-counterclockwise me-1" />
                    Reset Filters
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="row g-3">
                  {visibleJobs.map((job) => (
                    <JobCard
                      key={job.id}
                      job={job}
                    isSaved={savedJobs.includes(job.id)}
                    isApplied={appliedJobIds.has(job.id)}
                    canApply={subscription?.status === "active"}
                    onSave={toggleSave}
                    onApply={applyToJob}
                  />
                  ))}
                </div>

                {/* Pagination */}
                <div className="d-flex justify-content-between align-items-center mt-4 pt-2">
                  <button
                    className="btn btn-primary page-btn"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    title="Previous page"
                  >
                    <i className="bi bi-chevron-left" />
                  </button>

                  <div className="d-flex gap-1">
                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                      let p;
                      if (totalPages <= 7) {
                        p = i + 1;
                      } else if (currentPage <= 4) {
                        p = i + 1;
                        if (i === 6) p = totalPages;
                      } else if (currentPage >= totalPages - 3) {
                        p = i === 0 ? 1 : totalPages - 6 + i;
                      } else {
                        const map = [1, currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2, totalPages];
                        p = map[i];
                      }
                      const isEllipsis =
                        totalPages > 7 &&
                        ((i === 1 && p > 2) || (i === 5 && p < totalPages - 1));
                      if (isEllipsis) {
                        return (
                          <span
                            key={`ellipsis-${i}`}
                            className="page-btn text-muted"
                            style={{ cursor: "default" }}
                          >
                            …
                          </span>
                        );
                      }
                      return (
                        <button
                          key={p}
                          className={`btn page-btn ${p === currentPage ? "btn-primary" : "btn-outline-secondary"}`}
                          onClick={() => setPage(p)}
                        >
                          {p}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    className="btn btn-primary page-btn"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                    title="Next page"
                  >
                    <i className="bi bi-chevron-right" />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default SeekerJobs;
